import { Popover, PopoverContent, PopoverTrigger } from '@gettactic/components';
import { DragDropContext, DropResult, Droppable } from '@hello-pangea/dnd';
import { ArrowRight } from 'lucide-react';
import React from 'react';
import { useMapCanvas } from '../../../contexts/MapCanvasContext';
import { useSpotLabelIterator } from '../../../hooks/useSpotLabelIterator';
import { SpotLabelPatternItem } from '../../../types';
import { NumberingToolSortableItem } from './NumberingToolSortableItem';
import { TextInput } from '../../../../../../../components/src/form/TextInput';

const classes = {
  Button: 'bg-gray-100 rounded px-2 py-2 flex gap-1 items-center',
  Value: 'bg-muted inline-block rounded text-muted-foreground',
  PopoverContent: 'w-[600px] p-3'
};

export const NumberingPreviewTool: React.FC = () => {
  const mapCanvas = useMapCanvas();
  const label = useSpotLabelIterator();
  const spotLabelPattern = mapCanvas.spotLabelPattern;

  const nextLabel = React.useMemo(() => {
    return (label.next(false) || []).join('');
  }, [mapCanvas.lastSpotLabel]);

  const currentLabel = React.useMemo(() => {
    return mapCanvas.lastSpotLabel?.join('') || '';
  }, [mapCanvas.lastSpotLabel]);
  /*const addPattern = (type: SpotLabelPatternItem['type']) => {
    const newPattern: SpotLabelPatternItem =
      type === 'text'
        ? { type: 'text', value: '' }
        : type === 'numbers'
          ? { type: 'numbers', depth: 1 }
          : type === 'letters'
            ? { type: 'letters', depth: 1 }
            : { type: 'array', value: [] };

    setSpotLabelPattern([...spotLabelPattern, newPattern]);
  };*/

  const updatePattern = (
    index: string,
    updates: Partial<SpotLabelPatternItem>
  ) => {
    mapCanvas.setSpotLabelPattern((patterns) =>
      patterns.map(
        (pattern, i) =>
          (String(i) === index ? { ...pattern, ...updates } : pattern) as never
      )
    );
  };

  const removePattern = (index: string) => {
    mapCanvas.setSpotLabelPattern((patterns) =>
      patterns.filter((_, i) => String(i) !== index)
    );
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(spotLabelPattern);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    mapCanvas.setSpotLabelPattern(items);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className={classes.Button}>
          <span className={classes.Value}>{currentLabel}</span>
          <ArrowRight size={14} />
          <span className={classes.Value}>{nextLabel}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className={classes.PopoverContent} align="center">
        <div className="text-center font-bold">
          <span>Node Label Pattern</span>
          {/* TEMPORALLY REMOVED FOR sc-4283 */}
          {/*<div className="flex gap-2">
            <Button
              variant="blue"
              size="sm"
              onClick={() => addPattern('text')}
              icon={Type}
            >
              Add Text
            </Button>
            <Button
              variant="blue"
              size="sm"
              onClick={() => addPattern('numbers')}
              icon={Hash}
            >
              Add Numbers
            </Button>
            <Button
              variant="blue"
              size="sm"
              onClick={() => addPattern('letters')}
              icon={LetterText}
            >
              Add Letters
            </Button>
            <Button
              variant="blue"
              size="sm"
              onClick={() => addPattern('array')}
              icon={List}
            >
              Add Array
            </Button>
          </div>*/}
        </div>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="patterns">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="space-y-4"
              >
                {spotLabelPattern
                  // TODO - This filter must be removed when restoring the full pattern control
                  .filter((pattern) => pattern.type === 'text')
                  .map((pattern, index) => (
                    <NumberingToolSortableItem
                      key={index}
                      pattern={pattern}
                      index={index}
                      updatePattern={updatePattern}
                      removePattern={removePattern}
                    />
                  ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        <div>
          <TextInput
            label={'Current Label Number'}
            placeholder="Values (comma-separated)"
            value={String(mapCanvas.lastSpotLabel?.[1] || '0')}
            onChange={(e) => {
              mapCanvas.setLastSpotLabel([
                mapCanvas.lastSpotLabel?.[0] || '',
                String(e.target.value) || '0'
              ]);
            }}
          />
        </div>
        {spotLabelPattern.length === 0 && (
          <div className="py-8 text-center text-gray-500">
            No patterns added. Add one using the buttons above.
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
